/**
 * xp1_HybridD_v3 - "Depth Cinema V3-HD"
 * 
 * FULL EFFECTS VERSION for comparison:
 * - All visual effects enabled (backdrop-filter, will-change, full blur)
 * - Non-throttled scroll handler
 * - Maximum visual fidelity
 * - May have scroll jank on some devices
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@services/auth';
import './xp1_HybridD_v3.css';

// Desktop Images
const overviewImg = '/showcase/overview_page.png';
const accountsImg = '/showcase/accounts_page.png';
const analyticsImg = '/showcase/ana_page.png';
const categoryImg = '/showcase/category_page.png';
const settingsImg = '/showcase/settings_page.jpg';

// Mobile Images
const mobileOverviewImg = '/showcase/mobile_overview.png';
const mobileAccountsImg = '/showcase/mobile_accounts.png';
const mobileAnalyticsImg = '/showcase/mobile_ana.png';
const mobileSettingsImg = '/showcase/mobile_settings.png';

// Responsive image helper
interface ResponsiveImg {
  desktop: string;
  mobile: string;
}

const DEPTH_CARDS = [
  {
    id: 'visualize',
    num: '01',
    title: 'VISUALIZE',
    subtitle: 'Complete Overview',
    desc: 'See your entire financial reality in one stunning dashboard. Net worth, cash flow, and recent activity at a glance.',
    img: { desktop: overviewImg, mobile: mobileOverviewImg } as ResponsiveImg,
    color: '#7aa2f7',
  },
  {
    id: 'analyze',
    num: '02',
    title: 'ANALYZE',
    subtitle: 'Deep Insights',
    desc: 'Pattern recognition and cash flow scoring. Understand not just where money goes, but why.',
    img: { desktop: analyticsImg, mobile: mobileAnalyticsImg } as ResponsiveImg,
    color: '#bb9af7',
  },
  {
    id: 'organize',
    num: '03',
    title: 'ORGANIZE',
    subtitle: 'Smart Categories',
    desc: '64+ preset categories with custom icons. Structure your financial life your way.',
    img: { desktop: categoryImg, mobile: mobileAnalyticsImg } as ResponsiveImg,
    color: '#9ece6a',
  },
  {
    id: 'optimize',
    num: '04',
    title: 'OPTIMIZE',
    subtitle: 'Full Control',
    desc: 'Nine dark themes, privacy modes, and import/export. Make it truly yours.',
    img: { desktop: settingsImg, mobile: mobileSettingsImg } as ResponsiveImg,
    color: '#e0af68',
  },
];

const TESTIMONIALS = [
  {
    quote: "Finally, a finance app that works offline. I track expenses on flights without worrying about connectivity.",
    author: "Alex K.",
    role: "Frequent Traveler",
  },
  {
    quote: "The Tokyo Night themes are gorgeous. I actually enjoy opening my finance app now.",
    author: "Sarah M.",
    role: "Designer",
  },
  {
    quote: "No bank connections means no security worries. I control my data completely.",
    author: "James R.",
    role: "Privacy Advocate",
  },
];

export default function xp1_HybridD_v3() {
  const { signInWithGoogle, isLoading } = useAuth();
  const [scrollY, setScrollY] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    
    // Check if mobile
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // NON-THROTTLED scroll handler - full effects, may cause jank
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImg((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleGoogleSignIn = async () => {
    const { error } = await signInWithGoogle();
    if (error) {
      console.error('[LOGIN] Failed to sign in:', error.message);
    }
  };

  const heroImages = isMobile 
    ? [mobileAccountsImg, mobileOverviewImg, mobileAnalyticsImg]
    : [accountsImg, overviewImg, analyticsImg];
  const heroOpacity = Math.max(0, 1 - scrollY / 600);
  const heroScale = Math.max(0.8, 1 - scrollY / 3000);

  return (
    <div className={`xp1hd3 ${mounted ? 'mounted' : ''}`} ref={containerRef}>
      {/* Background Glow */}
      <div className="xp1hd3-bg-glow" />

      {/* Header */}
      <header className="xp1hd3-header">
        <div className="xp1hd3-logo">
          <span className="xp1hd3-logo-symbol">$</span>
          <span className="xp1hd3-logo-text">NINJACASH</span>
        </div>
        <nav className="xp1hd3-nav">
          <Link to="/xp1" className="xp1hd3-nav-link">Hub</Link>
          <button
            className="xp1hd3-nav-cta"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Launch'}
          </button>
        </nav>
      </header>

      {/* Cinematic Hero */}
      <section 
        className="xp1hd3-hero"
        style={{ 
          opacity: heroOpacity,
          transform: `scale(${heroScale})`,
        }}
      >
        <div className="xp1hd3-hero-content">
          <div className="xp1hd3-hero-tag">
            <span className="xp1hd3-tag-pulse" />
            PERSONAL FINANCE SYSTEM
          </div>

          <h1 className="xp1hd3-hero-title">
            <span className="sr-only">NinjaCash - Personal Finance Tracker</span>
            <span className="xp1hd3-title-line xp1hd3-anim-1" aria-hidden="true">NINJA</span>
            <span className="xp1hd3-title-line xp1hd3-title-glow xp1hd3-anim-2" aria-hidden="true">CASH</span>
          </h1>

          <p className="xp1hd3-hero-subtitle xp1hd3-anim-3">
            Track. Sync. Conquer.
          </p>

          <p className="xp1hd3-hero-desc xp1hd3-anim-4">
            The offline-first finance tracker that respects your privacy.
            No ads. No tracking. No bank connections. Just you and your money.
          </p>

          <div className="xp1hd3-hero-actions xp1hd3-anim-5">
            <button
              className="xp1hd3-btn-primary"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {isLoading ? 'Entering...' : 'Enter System'}
            </button>
          </div>

          {/* Enhanced Stats */}
          <div className="xp1hd3-hero-stats xp1hd3-anim-6">
            <div className="xp1hd3-stat">
              <span className="xp1hd3-stat-value">100%</span>
              <span className="xp1hd3-stat-label">Offline</span>
            </div>
            <div className="xp1hd3-stat-divider" />
            <div className="xp1hd3-stat">
              <span className="xp1hd3-stat-value">64+</span>
              <span className="xp1hd3-stat-label">Categories</span>
            </div>
            <div className="xp1hd3-stat-divider" />
            <div className="xp1hd3-stat">
              <span className="xp1hd3-stat-value">9</span>
              <span className="xp1hd3-stat-label">Themes</span>
            </div>
          </div>

          {/* Developer Credit */}
          <a 
            href="https://github.com/TakashiKyoto/NinjaCash" 
            target="_blank" 
            rel="noopener noreferrer"
            className="xp1hd3-dev-credit xp1hd3-anim-6"
          >
            Developed by Takashi on GitHub
          </a>
        </div>

        {/* 3D Device - responsive: desktop shows wider frame, mobile shows phone frame */}
        <div className={`xp1hd3-hero-device ${isMobile ? 'is-mobile' : 'is-desktop'}`}>
          <div className="xp1hd3-device-wrapper">
            <div className="xp1hd3-device-frame">
              {heroImages.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt="App preview"
                  className={`xp1hd3-device-img ${i === activeImg ? 'active' : ''}`}
                />
              ))}
            </div>
            <div className="xp1hd3-device-glow" />
            <div className="xp1hd3-device-reflection" />
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="xp1hd3-scroll-hint">
          <span>Scroll to explore</span>
          <div className="xp1hd3-scroll-line" />
        </div>
      </section>

      {/* Stream Section with Central Line */}
      <section className="xp1hd3-stream">
        <div className="xp1hd3-stream-line" />

        {/* Depth Cards */}
        <div className="xp1hd3-cards-container">
          {DEPTH_CARDS.map((card, index) => (
            <div
              key={card.id}
              className="xp1hd3-card-wrapper"
              style={{ 
                '--card-color': card.color,
                '--card-index': index,
              } as React.CSSProperties}
            >
              <div className="xp1hd3-card">
                <div className="xp1hd3-card-content">
                  <div className="xp1hd3-card-num">{card.num}</div>
                  <h2 className="xp1hd3-card-title">{card.title}</h2>
                  <h3 className="xp1hd3-card-subtitle">{card.subtitle}</h3>
                  <p className="xp1hd3-card-desc">{card.desc}</p>
                </div>
                <div className="xp1hd3-card-visual">
                  <div className="xp1hd3-card-img-frame">
                    <picture>
                      <source media="(max-width: 768px)" srcSet={card.img.mobile} />
                      <img src={card.img.desktop} alt={card.title} className="xp1hd3-card-img" />
                    </picture>
                  </div>
                </div>
                <div className="xp1hd3-card-glow" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="xp1hd3-testimonials">
        <h2 className="xp1hd3-section-title">What Users Say</h2>
        <div className="xp1hd3-testimonial-container">
          {TESTIMONIALS.map((t, i) => (
            <div 
              key={i} 
              className={`xp1hd3-testimonial ${i === activeTestimonial ? 'active' : ''}`}
            >
              <blockquote className="xp1hd3-testimonial-quote">
                "{t.quote}"
              </blockquote>
              <div className="xp1hd3-testimonial-author">
                <span className="xp1hd3-testimonial-name">{t.author}</span>
                <span className="xp1hd3-testimonial-role">{t.role}</span>
              </div>
            </div>
          ))}
          <div className="xp1hd3-testimonial-dots">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                className={`xp1hd3-testimonial-dot ${i === activeTestimonial ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(i)}
                aria-label={`View testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="xp1hd3-cta">
        <div className="xp1hd3-cta-content">
          <h2 className="xp1hd3-cta-title">
            <span className="xp1hd3-title-glow">READY?</span>
          </h2>
          <p className="xp1hd3-cta-desc">
            Enter the future of personal finance.
            Free forever for single device.
          </p>
          <button
            className="xp1hd3-btn-primary xp1hd3-btn-large"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {isLoading ? 'Initializing...' : 'Initialize System'}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="xp1hd3-footer">
        <div className="xp1hd3-footer-brand">
          <span className="xp1hd3-logo-symbol xp1hd3-logo-small">$</span>
          <span>NINJACASH</span>
        </div>
        <div className="xp1hd3-footer-links">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="https://github.com/TakashiKyoto/NinjaCash" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
        </div>
        <div className="xp1hd3-footer-copy">
          &copy; {new Date().getFullYear()} NinjaCash
        </div>
      </footer>
    </div>
  );
}
